import { Modal } from "@mantine/core";
import { useGhostNode } from "../../hooks/useGhostNode";
import { useGhostNodeMutation } from "../../hooks/useGhostNodeMutation";
import { useMissingObjectData } from "../../hooks/useMissingObjectData";
import { useToast } from "../../mantine/notifications/useToast";
import type { BaseWizardProps } from "../../types/wizard.types";
import type { GhostNode } from "../../utils/backend-types";
import { ObjectName } from "../../utils/enums";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { GhostNodeForm } from "./GhostNodeForm";
import "./GhostNodeWizard.scss";

type GhostNodeWizardProps = BaseWizardProps & { siteId: number };

function GhostNodeWizard({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  siteId,
  onClose,
  ...modeProps
}: GhostNodeWizardProps) {
  const { showToast } = useToast();
  const ghostNodeId =
    modeProps.mode === "edit" ? modeProps.objectId : undefined;

  const {
    data: ghostNode,
    isLoading: isGhostNodeLoading,
    error: ghostNodeError
  } = useGhostNode(ghostNodeId);

  const {
    isLoading: isMissingDataLoading,
    error: missingDataError,
    fieldsThatCouldBeMissing,
    shouldFieldBeHighlighted
  } = useMissingObjectData(ghostNodeId, siteId);

  const action = modeProps.mode === "create" ? "erstellen" : "bearbeiten";
  const title = ghostNode?.name
    ? `${ghostNode.name} ${action}`
    : `${ObjectName.GhostNode} ${action}`;

  const { mutateAsync } = useGhostNodeMutation(
    modeProps.mode,
    siteId,
    graphCoordinates
  );

  async function handleSubmit(formData: GhostNode) {
    return mutateAsync(formData, {
      onSuccess: () => {
        onClose();
        showToast({
          type: "success",
          message:
            modeProps.mode === "create"
              ? "Der Verteilungspunkt wurde erstellt."
              : "Die Änderungen wurden gespeichert."
        });
      }
    });
  }

  const isLoading =
    modeProps.mode === "edit" && (isGhostNodeLoading || isMissingDataLoading);
  const error =
    modeProps.mode === "edit" ? ghostNodeError || missingDataError : null;

  return (
    <Modal
      className="GhostNodeWizard"
      opened={isOpen}
      size="xl"
      title={title}
      onClose={onClose}
    >
      <LoadOrError error={error} loading={isLoading}>
        {modeProps.mode === "create" ? (
          <GhostNodeForm
            mode="create"
            onCancel={onClose}
            onSubmit={handleSubmit}
          />
        ) : ghostNode ? (
          <GhostNodeForm
            fieldsThatCouldBeMissing={fieldsThatCouldBeMissing}
            mode="edit"
            shouldFieldBeHighlighted={shouldFieldBeHighlighted}
            wizardObject={ghostNode}
            onCancel={onClose}
            onSubmit={handleSubmit}
          />
        ) : null}
      </LoadOrError>
    </Modal>
  );
}

export { GhostNodeWizard, GhostNodeWizardProps };
